/**
 * Validates a single email domain entry for CostPayoutModifier conditions.
 *
 * Accepts two formats (matching how the materialized view uses them via LIKE CONCAT('%', domain)):
 *   - "example.com"         bare domain suffix
 *   - "@hellojuniper.com"   @-prefixed exact domain
 *
 * Input is expected to be lowercased before testing.
 */
export const EMAIL_DOMAIN_PATTERN = /^@?[a-z0-9]([a-z0-9.-]*[a-z0-9])?\.[a-z]{2,}$/;

export const isValidEmailDomain = (domain: string): boolean => {
    return EMAIL_DOMAIN_PATTERN.test(domain);
};

/**
 * Country-code regex: exactly 2 uppercase ASCII letters.
 */
export const COUNTRY_CODE_PATTERN = /^[A-Z]{2}$/;

/**
 * Normalize a country code string: trim and uppercase.
 * Returns empty string for falsy input.
 */
export function normalizeCountryCode(value: string | null | undefined): string {
    if (!value) return "";
    return value.trim().toUpperCase();
}

/**
 * Check whether a normalized (non-empty) country code is valid.
 */
export function isValidCountryCode(normalized: string): boolean {
    return COUNTRY_CODE_PATTERN.test(normalized);
}

/**
 * Parse a raw comma-separated email domain string into cleaned entries.
 * Trims, lowercases, removes blanks, and deduplicates.
 *
 * Use this on both client (form submit, inline edit, validation display)
 * and server (pre-validation normalization) to avoid duplicating the
 * split -> trim -> lowercase -> filter -> dedup pipeline.
 */
export function parseEmailDomainInput(raw: string): string[] {
    return Array.from(new Set(
        raw.split(",")
            .map(d => d.trim().toLowerCase())
            .filter(d => d.length > 0)
    ));
}

/**
 * Sanitize a country code for CostPayoutModifier conditions.
 *
 * - undefined in -> undefined out (field not sent, skip update)
 * - null/empty in -> null out (field explicitly cleared)
 * - non-empty in -> uppercased, validated 2-letter ISO code (or throws)
 */
export function sanitizeCountryCode(value: string | undefined | null): string | null | undefined {
    if (value === undefined) return undefined;
    const normalized = normalizeCountryCode(value);
    if (!normalized) return null;
    if (!isValidCountryCode(normalized)) {
        throw new Error("conditionCountryCode must be a 2-character ISO country code (e.g., US, CA).");
    }
    return normalized;
}

/**
 * Sanitize email domains for CostPayoutModifier conditions.
 *
 * - undefined in -> undefined out (field not sent, skip update)
 * - null/empty in -> [] out (field explicitly cleared)
 * - non-empty in -> lowercased, deduplicated, validated entries (or throws)
 */
export function sanitizeEmailDomains(value: string[] | undefined | null): string[] | undefined {
    if (value === undefined) return undefined;
    if (!value || value.length === 0) return [];
    const deduplicated = parseEmailDomainInput(value.join(","));
    const invalid = deduplicated.find(d => !isValidEmailDomain(d));
    if (invalid) {
        throw new Error(`Invalid email domain: "${invalid}". Expected format: example.com or @example.com (lowercase, valid TLD).`);
    }
    return deduplicated;
}
